import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'

type Employment = {
  id: number
  employee_number: string
  person_name: string
  status: 'PRE_HIRE' | 'ACTIVE' | 'ON_LEAVE' | 'ENDED'
}

type TimeOff = {
  id: number
  employment: number
  person_name: string
  employment_number: string
  leave_type: 'VACATION' | 'SICK' | 'UNPAID' | 'PARENTAL' | 'OTHER'
  start_date: string
  end_date: string
  days: string
  reason: string
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  approved_by_username: string | null
  approved_at: string | null
  notes: string
}

type TimeOffInput = {
  employment: number
  leave_type: TimeOff['leave_type']
  start_date: string
  end_date: string
  days: string
  reason: string
  status: TimeOff['status']
  notes: string
}

const TYPE_LABEL: Record<TimeOff['leave_type'], string> = {
  VACATION: 'Vacation',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  PARENTAL: 'Parental leave',
  OTHER: 'Other',
}

const STATUS_COLOR: Record<TimeOff['status'], string> = {
  REQUESTED: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
}

export default function HRTimeOff() {
  const qc = useQueryClient()
  const requestsQ = useQuery({
    queryKey: ['hr', 'time-off'],
    queryFn: async () => (await api.get<TimeOff[]>('/hr/time-off/')).data,
  })
  const employmentsQ = useQuery({
    queryKey: ['hr', 'employments'],
    queryFn: async () => (await api.get<Employment[]>('/hr/employments/')).data,
  })

  const [editing, setEditing] = useState<TimeOff | null>(null)
  const [creating, setCreating] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('ALL')

  const createMut = useMutation({
    mutationFn: async (p: TimeOffInput) => (await api.post('/hr/time-off/', p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }); setCreating(false) },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: TimeOffInput }) =>
      (await api.patch(`/hr/time-off/${id}/`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }); setEditing(null) },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/time-off/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }),
  })
  const approveMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/hr/time-off/${id}/approve/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }),
  })
  const rejectMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/hr/time-off/${id}/reject/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }),
  })
  const cancelMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/hr/time-off/${id}/cancel/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'time-off'] }),
  })

  const requests = requestsQ.data || []
  const employments = (employmentsQ.data || []).filter(
    (e) => e.status === 'ACTIVE' || e.status === 'ON_LEAVE'
  )
  const filtered = filterStatus === 'ALL' ? requests : requests.filter((r) => r.status === filterStatus)

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Time Off</h2>
          <p className="text-sm text-gray-500">{requests.length} total · {requests.filter((r) => r.status === 'REQUESTED').length} pending</p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={employments.length === 0}>+ New request</Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-gray-500">Filter:</span>
        {(['ALL', 'REQUESTED', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {requestsQ.isLoading ? <p className="text-gray-500">Loading…</p> : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No time-off requests.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Employee</Th>
                <Th>Type</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th className="text-right">Days</Th>
                <Th>Status</Th>
                <Th>Approved by</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <Td>
                    <div className="font-medium">{r.person_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{r.employment_number}</div>
                  </Td>
                  <Td className="text-xs">{TYPE_LABEL[r.leave_type]}</Td>
                  <Td className="text-xs">{r.start_date}</Td>
                  <Td className="text-xs">{r.end_date}</Td>
                  <Td className="text-right font-mono">{Number(r.days).toFixed(1)}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[r.status]}`}>{r.status}</span>
                  </Td>
                  <Td className="text-xs">{r.approved_by_username || '—'}</Td>
                  <Td>
                    {r.status === 'REQUESTED' && (
                      <>
                        <button onClick={() => approveMut.mutate(r.id)}
                          className="text-green-700 hover:underline text-sm mr-2 font-medium">Approve</button>
                        <button onClick={() => rejectMut.mutate(r.id)}
                          className="text-red-600 hover:underline text-sm mr-2">Reject</button>
                      </>
                    )}
                    {(r.status === 'REQUESTED' || r.status === 'APPROVED') && (
                      <button onClick={() => { if (confirm('Cancel this request?')) cancelMut.mutate(r.id) }}
                        className="text-amber-700 hover:underline text-sm mr-2">Cancel</button>
                    )}
                    <button onClick={() => setEditing(r)} className="text-blue-600 hover:underline text-sm mr-2">Edit</button>
                    <button onClick={() => { if (confirm(`Delete request?`)) deleteMut.mutate(r.id) }}
                      className="text-red-600 hover:underline text-sm">Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <TimeOffDialog
          initial={editing ? {
            employment: editing.employment, leave_type: editing.leave_type,
            start_date: editing.start_date, end_date: editing.end_date,
            days: editing.days, reason: editing.reason, status: editing.status,
            notes: editing.notes,
          } : {
            employment: employments[0]?.id ?? 0, leave_type: 'VACATION',
            start_date: new Date().toISOString().slice(0, 10),
            end_date: new Date().toISOString().slice(0, 10),
            days: '1', reason: '', status: 'REQUESTED', notes: '',
          }}
          isEdit={!!editing}
          employments={employments}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(p) => {
            if (editing) updateMut.mutate({ id: editing.id, payload: p })
            else createMut.mutate(p)
          }}
        />
      )}
    </AdminLayout>
  )
}

function TimeOffDialog({
  initial, isEdit, employments, onSubmit, onCancel, submitting, error,
}: {
  initial: TimeOffInput
  isEdit: boolean
  employments: Employment[]
  onSubmit: (p: TimeOffInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<TimeOffInput>(initial)
  const set = <K extends keyof TimeOffInput>(k: K, v: TimeOffInput[K]) => setForm((f) => ({ ...f, [k]: v }))

  // Helper: auto-compute days when start/end change
  const computeDays = () => {
    if (form.start_date && form.end_date) {
      const start = new Date(form.start_date)
      const end = new Date(form.end_date)
      const diffMs = end.getTime() - start.getTime()
      const days = Math.max(1, Math.floor(diffMs / 86400000) + 1)
      set('days', String(days))
    }
  }

  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit time-off' : 'New time-off request'}</h3>
        <form onSubmit={submit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="to-employee">Employment *</FieldLabel>
              <SearchableSelect
                id="to-employee"
                value={form.employment ? String(form.employment) : undefined}
                onChange={(v) => set('employment', Number(v))}
                placeholder="— select —"
                options={employments.map((e) => ({
                  value: String(e.id), label: e.person_name, hint: e.employee_number,
                }))}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="to-leave-type">Leave type *</FieldLabel>
              <Select
                value={form.leave_type}
                onValueChange={(v) => set('leave_type', v as TimeOffInput['leave_type'])}
              >
                <SelectTrigger id="to-leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="VACATION">Vacation</SelectItem>
                  <SelectItem value="SICK">Sick leave</SelectItem>
                  <SelectItem value="UNPAID">Unpaid leave</SelectItem>
                  <SelectItem value="PARENTAL">Parental leave</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="to-start">Start date *</FieldLabel>
                <DatePicker id="to-start" required value={form.start_date}
                  onChange={(v) => { set('start_date', v); setTimeout(computeDays, 0) }} />
              </Field>
              <Field>
                <FieldLabel htmlFor="to-end">End date *</FieldLabel>
                <DatePicker id="to-end" required value={form.end_date}
                  onChange={(v) => { set('end_date', v); setTimeout(computeDays, 0) }} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="to-days">Days *</FieldLabel>
              <Input id="to-days" type="number" step="0.5" required value={form.days}
                onChange={(e) => set('days', e.target.value)} className="font-mono" />
            </Field>
            {isEdit && (
              <Field>
                <FieldLabel htmlFor="to-status">Status</FieldLabel>
                <Select
                  value={form.status}
                  onValueChange={(v) => set('status', v as TimeOffInput['status'])}
                >
                  <SelectTrigger id="to-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REQUESTED">Requested</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="to-reason">Reason</FieldLabel>
              <Textarea id="to-reason" value={form.reason} onChange={(e) => set('reason', e.target.value)} rows={2} />
            </Field>
            <Field>
              <FieldLabel htmlFor="to-notes">Notes (HR)</FieldLabel>
              <Textarea id="to-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
            </Field>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-3">
              <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </FieldGroup>
        </form>
      </div>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
